import ExcelJS from 'exceljs';

export const DARB_LOGO_PATH = '/cropped-pixelcut-export-removebg-2048x691.png';

export const DARB_COMPANY_LINES = [
  'DARB NATIONAL SERVICE LLC',
  'P.O Box 417, PC 100, Muscat, Sultanate of Oman',
  'Phone: +968 24790888 | Email: info@darbns.com',
  'CR: 1366462',
];

export interface DarbExcelColumn {
  header: string;
  key: string;
  width?: number;
}

export interface DarbExcelExportOptions {
  sheetName: string;
  title: string;
  subtitleLines?: string[];
  columns: DarbExcelColumn[];
  rows: Record<string, unknown>[];
  fileName: string;
}

async function fetchLogoBuffer(): Promise<ArrayBuffer | null> {
  try {
    const response = await fetch(DARB_LOGO_PATH);
    if (!response.ok) return null;
    return response.arrayBuffer();
  } catch {
    return null;
  }
}

function toCellValue(value: unknown): ExcelJS.CellValue {
  if (value === null || value === undefined) return '';
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value instanceof Date
  ) {
    return value;
  }
  return String(value);
}

/**
 * Builds and downloads an Excel report with the DARB logo and letterhead at the top.
 */
export async function downloadDarbExcelReport(options: DarbExcelExportOptions): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(options.sheetName);

  const logoBuffer = await fetchLogoBuffer();
  const logoRowCount = 5;

  if (logoBuffer) {
    const imageId = workbook.addImage({
      buffer: logoBuffer,
      extension: 'png',
    });
    worksheet.addImage(imageId, {
      tl: { col: 0.5, row: 0.2 },
      ext: { width: 220, height: 74 },
    });
  }

  for (let i = 1; i <= logoRowCount; i += 1) {
    worksheet.getRow(i).height = i === 1 ? 58 : 14;
  }

  let rowIndex = logoRowCount + 1;
  DARB_COMPANY_LINES.forEach((line) => {
    const row = worksheet.getRow(rowIndex);
    row.getCell(1).value = line;
    row.getCell(1).font = rowIndex === logoRowCount + 1
      ? { bold: true, size: 12, name: 'Calibri' }
      : { size: 10, name: 'Calibri' };
    row.getCell(1).alignment = { horizontal: 'center' };
    worksheet.mergeCells(rowIndex, 1, rowIndex, options.columns.length);
    rowIndex += 1;
  });

  rowIndex += 1;
  const titleRow = worksheet.getRow(rowIndex);
  titleRow.getCell(1).value = options.title;
  titleRow.getCell(1).font = { bold: true, size: 14, name: 'Calibri' };
  titleRow.getCell(1).alignment = { horizontal: 'center' };
  worksheet.mergeCells(rowIndex, 1, rowIndex, options.columns.length);
  rowIndex += 1;

  if (options.subtitleLines?.length) {
    options.subtitleLines.forEach((line) => {
      const row = worksheet.getRow(rowIndex);
      row.getCell(1).value = line;
      row.getCell(1).font = { size: 10, name: 'Calibri', italic: true };
      row.getCell(1).alignment = { horizontal: 'center' };
      worksheet.mergeCells(rowIndex, 1, rowIndex, options.columns.length);
      rowIndex += 1;
    });
  }

  rowIndex += 1;
  const headerRow = worksheet.getRow(rowIndex);
  options.columns.forEach((col, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = col.header;
    cell.font = { bold: true, size: 10, name: 'Calibri', color: { argb: 'FFFFFFFF' } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1E1E1E' },
    };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
    worksheet.getColumn(index + 1).width = col.width ?? 18;
  });
  headerRow.height = 22;

  options.rows.forEach((data) => {
    rowIndex += 1;
    const row = worksheet.getRow(rowIndex);
    options.columns.forEach((col, index) => {
      const cell = row.getCell(index + 1);
      cell.value = toCellValue(data[col.key]);
      cell.font = { size: 10, name: 'Calibri' };
      cell.alignment = { vertical: 'top', wrapText: true };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = options.fileName;
  link.click();
  URL.revokeObjectURL(url);
}
